/**
 * The two dates the platform derives from the closing date and the term
 * (Chris Young, 26 Oct 2026): Maturity is the closing date plus the initial term,
 * Extension Maturity the closing date plus the fully extended term.
 *
 * Mirrors `backend/app/underwriting/dealfields.py` `DATE_DERIVABLE_FIELDS`.
 *
 * Unlike a derived ratio, a derived date can be STATED over: a loan document
 * states its maturity, and a stated maturity wins and is never replaced by the
 * formula. So an unlocked dossier offers an input on these even while they read
 * Derived (R1 - "people correct inputs, not outputs" - is for the ratios).
 */
import { stateOfLineage } from '@/components/fields/lineage'
import type { LoanFieldLineageField } from '@/pages/pipeline-intelligence/types'

export const DERIVED_DATE_FIELDS: ReadonlySet<string> = new Set(['maturity_date', 'extension_maturity_date'])

/**
 * The dates whose edits move a derived date: the closing date a rule counts from, and the two
 * derived dates themselves (a maturity being stated). Mirrors `DATE_RULE_INPUTS` and
 * `DATE_DERIVABLE_FIELDS` in `backend/app/underwriting/dealfields.py`; the derivation preview reads these.
 */
export const DATE_PREVIEW_FIELDS: ReadonlySet<string> = new Set(['closing_date', 'maturity_date', 'extension_maturity_date'])

export function isDerivedDateField(name: string): boolean {
  return DERIVED_DATE_FIELDS.has(name)
}

/**
 * The stored values the engine derived (lineage state Derived), by field, with
 * the formula each was computed by: outputs, which an unlocked dossier shows
 * and never types over (R1, nd-030) - except a derived date, which is offered as
 * an input because typing it states it.
 */
export function derivedFormulasOf(fields: readonly LoanFieldLineageField[]): Map<string, string> {
  const out = new Map<string, string>()
  for (const field of fields) {
    if (stateOfLineage(field) !== 'derived') continue
    if (isDerivedDateField(field.fieldName)) continue
    out.set(field.fieldName, field.derivation?.formula || 'computed from other fields')
  }
  return out
}
